import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Badge, { type BadgeProps } from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import Table from '@cloudscape-design/components/table';
import { type CancellationReason } from '@etiquetachef/types';
import { categorizeChurnReasonCallable } from '@/lib/callables';
import {
  formatCurrencyCents,
  formatInt,
  formatUsdCents,
} from '@/features/dashboard-finance/formatters';
import { CategorizeReasonDialog } from './CategorizeReasonDialog';
import type { ChurnedTenantRow } from './useChurnedTenants';

function formatDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

const REASON_COLOR: Record<CancellationReason, NonNullable<BadgeProps['color']>> = {
  USER_REQUEST: 'grey',
  PAYMENT_FAILED: 'red',
  INACTIVITY: 'blue',
  UNKNOWN: 'grey',
};

/**
 * Tabela dos tenants cancelados no período, com o motivo categorizado e a
 * ação de (re)categorizar.
 *
 * Origem: sem issue — commit b9c02cd45, 2026-05-04 (rota /dashboard/churn).
 *
 * Contrato: `rows` vêm do `useChurnedTenants`; ao salvar um motivo chama a
 * callable `categorizeChurnReason({ auditId, reason })` e depois
 * `onChanged` (o `refetch` da página). Erro da callable sobe para o
 * `CategorizeReasonDialog`, que mostra e reporta.
 *
 * Usado por: `ChurnDashboardPage.tsx` (rota `/dashboard/churn`).
 *
 * Arquitetura: o doc de `audit` é read-only nas rules; o motivo só entra pela
 * callable (`apps/functions/src/admin/categorizeChurnReason.ts`, exige
 * super-admin), que grava `cancellationReason` e o
 * `cancellationCategorizedAt` com server timestamp.
 *
 * Histórico:
 *  - 2026-06-16 — (#13 §3.4.1) escrita direta no Firestore trocada pela
 *    callable (b52617ebd).
 *
 * Issues: #13.
 */
export function ChurnTable({
  rows,
  onChanged,
}: {
  rows: readonly ChurnedTenantRow[];
  onChanged?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<ChurnedTenantRow | null>(null);

  async function persistReason(row: ChurnedTenantRow, reason: CancellationReason): Promise<void> {
    // §3.4.1 (#13): a categorização vai por callable dedicada. O doc de audit
    // é read-only nas Rules e `cancellationCategorizedAt` tem que ser server
    // timestamp — escrita direta no Firestore a partir daqui é proibida.
    await categorizeChurnReasonCallable({ auditId: row.auditId, reason });
    onChanged?.();
  }

  return (
    <>
      <Table<ChurnedTenantRow>
        items={[...rows]}
        trackBy="auditId"
        variant="container"
        header={<Header variant="h3">{t('churn.table.title', { count: rows.length })}</Header>}
        empty={<Box textAlign="center" color="inherit">{t('churn.empty')}</Box>}
        columnDefinitions={[
          { id: 'tenant', header: t('churn.table.tenant'), cell: (r): React.ReactNode => r.tenantName },
          { id: 'country', header: t('churn.table.country'), cell: (r): React.ReactNode => r.countryCode },
          {
            id: 'price',
            header: t('churn.table.price'),
            cell: (r): React.ReactNode =>
              r.currency ? formatCurrencyCents(r.priceCents, r.currency) : formatInt(r.priceCents),
          },
          {
            id: 'usdEq',
            header: t('churn.table.usdEq'),
            cell: (r): React.ReactNode => formatUsdCents(r.usdEqCents),
          },
          {
            id: 'lifetime',
            header: t('churn.table.lifetime'),
            cell: (r): React.ReactNode => (r.lifetimeDays == null ? '—' : formatInt(r.lifetimeDays)),
          },
          {
            id: 'suspendedAt',
            header: t('churn.table.suspendedAt'),
            cell: (r): React.ReactNode => formatDate(r.suspendedAt),
          },
          {
            id: 'reason',
            header: t('churn.table.reason'),
            cell: (r): React.ReactNode =>
              r.reason ? (
                <Badge color={REASON_COLOR[r.reason]}>{t(`churn.reason.${r.reason}`)}</Badge>
              ) : (
                <Box fontSize="body-s" color="text-status-inactive">
                  {t('churn.table.uncategorized')}
                </Box>
              ),
          },
          {
            id: 'actions',
            header: t('churn.table.actions'),
            cell: (r): React.ReactNode => (
              <Button
                onClick={(): void => {
                  setEditing(r);
                }}
              >
                {t('churn.table.categorize')}
              </Button>
            ),
          },
        ]}
      />
      {editing && (
        <CategorizeReasonDialog
          open={!!editing}
          onClose={(): void => {
            setEditing(null);
          }}
          tenantName={editing.tenantName}
          suggested={editing.suggestedReason}
          initial={editing.reason}
          onSave={async (reason): Promise<void> => {
            await persistReason(editing, reason);
          }}
        />
      )}
    </>
  );
}
